import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Send, Search } from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Wallet P2P send sheet (iter219).
 *
 * Two modes:
 * - Standalone: pass no `prefill` prop, we fetch eligible recipients & let user pick.
 * - Prefilled (from group Settle button): pass `prefill={ to_user_id, to_name, amount, group_id, note }`.
 *
 * Balance is fetched on open; button disables when amount > balance.
 */
export default function WalletSendSheet({ open, onOpenChange, prefill, onSent }) {
  const [balance, setBalance] = useState(0);
  const [recipients, setRecipients] = useState([]);
  const [toUserId, setToUserId] = useState(prefill?.to_user_id || '');
  const [amount, setAmount] = useState(prefill?.amount ? String(prefill.amount) : '');
  const [note, setNote] = useState(prefill?.note || '');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setToUserId(prefill?.to_user_id || '');
    setAmount(prefill?.amount ? String(prefill.amount) : '');
    setNote(prefill?.note || '');
    setSearch('');
    setLoading(true);
    Promise.all([
      api.get('/wallet/me'),
      prefill?.to_user_id ? Promise.resolve({ data: { rows: [] } }) : api.get('/wallet/eligible-recipients'),
    ]).then(([w, r]) => {
      setBalance(w.data.wallet?.balance || 0);
      setRecipients(r.data.rows || []);
    }).catch(() => {
      toast.error('Failed to load wallet data');
    }).finally(() => setLoading(false));
  }, [open, prefill?.to_user_id]);

  const amt = parseFloat(amount) || 0;
  const shortfall = amt > balance;
  const canSend = toUserId && amt > 0 && !shortfall && !submitting;

  const submit = async () => {
    if (!canSend) return;
    setSubmitting(true);
    try {
      const payload = { to_user_id: toUserId, amount: amt, note: note || undefined };
      if (prefill?.group_id) payload.group_id = prefill.group_id;
      const r = await api.post('/wallet/transfer', payload);
      toast.success(`Sent ${fmtINR(amt)} to ${r.data.to_user_name}`);
      onOpenChange(false);
      if (onSent) onSent(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredRecipients = recipients.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.name || '').toLowerCase().includes(s) || (r.phone || '').includes(s);
  });

  const selectedRecipient = prefill?.to_name
    ? { name: prefill.to_name }
    : recipients.find((r) => r.id === toUserId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="wallet-send-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-4 h-4 text-primary" />
            {prefill?.group_id ? 'Settle via wallet' : 'Send from wallet'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Balance */}
          <div className="rounded-lg bg-primary/5 border border-primary/20 p-3">
            <div className="text-xs text-muted-foreground">Your wallet balance</div>
            <div className="font-display text-2xl font-bold tabular-nums" data-testid="wallet-send-balance">
              {loading ? '…' : fmtINR(balance)}
            </div>
          </div>

          {/* Recipient (prefilled from group settle, or picker) */}
          {selectedRecipient ? (
            <div>
              <Label>Recipient</Label>
              <div className="mt-1 rounded-lg border-2 border-primary/30 bg-primary/5 px-3 py-2 font-semibold" data-testid="wallet-send-recipient">
                {selectedRecipient.name}
              </div>
            </div>
          ) : (
            <div>
              <Label>Send to</Label>
              <div className="relative mt-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search name or phone"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="wallet-send-search"
                />
              </div>
              <div className="mt-2 max-h-48 overflow-y-auto space-y-1 border rounded-lg p-1">
                {filteredRecipients.length === 0 && (
                  <div className="p-3 text-center text-sm text-muted-foreground">No matches</div>
                )}
                {filteredRecipients.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setToUserId(r.id)}
                    data-testid={`recipient-${r.id}`}
                    className={`w-full text-left px-3 py-2 rounded-md transition ${
                      toUserId === r.id ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                    }`}
                  >
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs opacity-70">{r.phone}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Amount */}
          <div>
            <Label>Amount (₹)</Label>
            <Input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              data-testid="wallet-send-amount"
              disabled={!!prefill?.amount}
            />
            {shortfall && (
              <p className="text-xs text-rose-600 mt-1" data-testid="wallet-send-shortfall">
                Insufficient balance — you have {fmtINR(balance)}
              </p>
            )}
          </div>

          {/* Note */}
          <div>
            <Label>Note (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What's this for?"
              data-testid="wallet-send-note"
              rows={2}
            />
          </div>

          {prefill?.group_id && (
            <div className="text-xs text-muted-foreground bg-emerald-500/5 border border-emerald-500/20 rounded-md p-2">
              ✅ This will also clear your <b>{prefill.group_name || 'group'}</b> balance for this settlement.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!canSend} data-testid="wallet-send-submit">
            {submitting ? 'Sending…' : `Send ${amt > 0 ? fmtINR(amt) : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
